import type { FolderData, Item, TagData } from "@aspect/plugin-sdk";

import request from "@/shared/api/request";
import { DEFAULT_PAGE_SIZE } from "@/shared/constants/pagination";

import type { ServiceHandler } from "../bridge/message-router";

type ItemsResponse = {
  items?: Item[];
};

type FoldersResponse = {
  folders?: FolderData[];
};

type TagsResponse = {
  tags?: TagData[];
};

/**
 * 数据服务：将插件对「我的」内容的 CRUD 与元数据查询转发到宿主 `mineApi`，在 Bridge 侧统一鉴权与错误形态。
 *
 * 部分方法（如按 id 查单条、选中项）受宿主能力限制，实现为占位或简化策略，与 SDK 文档需保持一致。
 */
export const dataServiceHandler: ServiceHandler = async (_pluginId, method, params) => {
  switch (method) {
    case "getItems": {
      /* 分页列表：可按文件夹或标签筛选；total 当前为当页条数（与现有接口返回一致） */
      const { folder_id, page = 1, pageSize = DEFAULT_PAGE_SIZE } = (params ?? {}) as {
        folder_id?: string;
        page?: number;
        pageSize?: number;
      };
      const response = await request.get<ItemsResponse>("/api/v1/items", {
        page,
        limit: pageSize,
        ...(folder_id ? { folder_id } : {}),
      });
      return {
        items: response.items ?? [],
        total: response.items?.length ?? 0,
      };
    }

    case "deleteItem": {
      /* 调用宿主删除接口移除指定条目 */
      const { id } = (params ?? {}) as { id: string };
      await request.post("/api/v1/items/deletions", { ids: [id] });
      return;
    }

    case "getFolders": {
      /* 拉取文件夹元数据，供插件侧文件夹筛选或展示 */
      const response = await request.get<FoldersResponse>("/api/v1/folders");
      return response.folders ?? [];
    }

    case "getTags": {
      /* 拉取标签全量列表，供筛选或标注场景 */
      const response = await request.get<TagsResponse>("/api/v1/tags");
      return response.tags ?? [];
    }

    default:
      throw Object.assign(new Error(`未知 data 方法: ${method}`), { code: "UNKNOWN_METHOD" });
  }
};
